import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import throttle from 'lodash/throttle';
import { __ } from '@wordpress/i18n';
import getRemBase from 'propel/getRemBase';

const translationButtonLabels = {
	en: __('ENG', 'propel'),
	es: __('SPN', 'propel'),
};

const openEvent = new Event('navigation-opened');
const closeEvent = new Event('navigation-closed');

const scrollableElements = document.querySelectorAll(
	'.block-navigation__menu, .block-navigation.is-style-full-width-dropdown .block-navigation-submenu'
);

const navigationBlocks = document.querySelectorAll('.block-navigation');

const navigationHamburgers = document.querySelectorAll(
	'.block-navigation__hamburger'
);

const contentArea = document.querySelector(
	'.content-wrapper, .content-wrapper--search, #primary.content-area'
);

const translationToggleDropdownButton = document.querySelector(
	'.block-navigation-top__translation-button'
);

const toggleNavigationHamburger = (e) => {
	const currentNavigationHamburger = e?.currentTarget ?? e;
	currentNavigationHamburger.classList.toggle('active');

	if (currentNavigationHamburger.classList.contains('active')) {
		window.dispatchEvent(openEvent);
		setFirstLevelButtonBlockOffsets();
		setNavMenuActiveClass(currentNavigationHamburger, true);
		currentNavigationHamburger.setAttribute('aria-expanded', true);

		if (window.innerWidth < 992) {
			lockScroll();
		}
	} else {
		window.dispatchEvent(closeEvent);

		clearAllBodyScrollLocks();

		setNavMenuActiveClass(currentNavigationHamburger, false);
		currentNavigationHamburger.setAttribute('aria-expanded', false);
	}
};

const submenuClosed = () => {
	if (window.innerWidth >= 992) {
		clearAllBodyScrollLocks();
	}
};

const setFirstLevelButtonBlockOffsets = () => {
	if (0 === navigationBlocks.length) {
		return;
	}

	navigationBlocks.forEach((navigationBlock) => {
		const firstLevelButtonBlocks = navigationBlock.querySelectorAll(
			'.block-navigation__menu > .wp-block-button'
		);

		if (0 === firstLevelButtonBlocks.length) {
			return;
		}

		let totalOffset = 0;

		for (let i = firstLevelButtonBlocks.length - 1; i >= 0; i--) {
			const firstLevelButtonBlock = firstLevelButtonBlocks[i];

			firstLevelButtonBlock.style.setProperty(
				'--buttonOffset',
				`${totalOffset / getRemBase()}rem`
			);

			totalOffset += firstLevelButtonBlock.offsetHeight;
		}

		navigationBlock.style.setProperty(
			'--totalButtonOffset',
			`${totalOffset / getRemBase()}rem`
		);
	});
};

const setNavMenuActiveClass = (element, navActive = null) => {
	const currentElement = element?.detail?.currentElement ?? element;

	if (!currentElement) {
		return;
	}

	const currentNavigationBlock = currentElement.closest('.block-navigation');

	if (!currentNavigationBlock) {
		return;
	}

	if (null === navActive) {
		if (
			currentElement.classList.contains('active') ||
			currentElement.parentElement.classList.contains('active') ||
			currentNavigationBlock.querySelector(
				'.block-navigation__hamburger.active'
			)
		) {
			navActive = true;
			if (contentArea) {
				contentArea.classList.add('active');
			}
		} else {
			navActive = false;
			if (contentArea) {
				contentArea.classList.remove('active');
			}
		}
	}

	if (true === navActive) {
		currentNavigationBlock.classList.add('active');
		document.addEventListener('keydown', clickOffMenu);
		document.addEventListener('click', clickOffMenu);
	} else {
		currentNavigationBlock.classList.remove('active');
		document.removeEventListener('keydown', clickOffMenu);
		document.removeEventListener('click', clickOffMenu);
	}

	navigationBlocks.forEach((navigationBlock) => {
		if (navigationBlock !== currentNavigationBlock) {
			navigationBlock.classList.remove('active');
		}
	});
};

const lockScroll = () => {
	if (scrollableElements.length) {
		scrollableElements.forEach(function (scrollableElement) {
			const storedRequestAnimationFrame = window.requestAnimationFrame;
			window.requestAnimationFrame = () => 42;

			disableBodyScroll(scrollableElement, {
				reserveScrollBarGap: true,
			});
			window.requestAnimationFrame = storedRequestAnimationFrame;
		});
	}
};

const clickOffMenu = (e) => {
	if (0 === navigationHamburgers.length) {
		return;
	}

	let closestMenu;
	let closestHamburger;

	if (e.key === undefined && e.target) {
		closestMenu = e.target.closest('.block-navigation__menu');
		closestHamburger = e.target.closest('.block-navigation__hamburger');
	}

	if (e.key && e.key !== 'Escape') {
		return;
	}

	if (
		closestMenu?.contains(e.target) ||
		closestHamburger?.contains(e.target)
	) {
		return;
	}

	navigationHamburgers.forEach((navigationHamburger) => {
		if (navigationHamburger.classList.contains('active')) {
			toggleNavigationHamburger(navigationHamburger);
		}
	});

	window.dispatchEvent(closeEvent);
};

if (navigationHamburgers.length > 0) {
	navigationHamburgers.forEach((navigationHamburger) => {
		navigationHamburger.addEventListener(
			'click',
			toggleNavigationHamburger
		);
	});
}

setFirstLevelButtonBlockOffsets();

const updateTranslationButtonLabel = () => {
	if (!translationToggleDropdownButton) {
		return;
	}

	const currentLangLink = document.querySelector(
		'.gtranslate_dropdown .glink.gt-current-lang'
	);

	const currentLang = currentLangLink?.getAttribute('data-gt-lang');

	if (currentLang && translationButtonLabels[currentLang]) {
		translationToggleDropdownButton.textContent =
			translationButtonLabels[currentLang];
	}
};

const handleGtranslateClick = () => {
	toggleLanguageDropdown();
	updateTranslationButtonLabel();
};

const toggleLanguageDropdown = () => {
	// console.log('Toggling language dropdown');
	const languageSwitcher = translationToggleDropdownButton.closest(
		'.navigation-language'
	);
	const currentNavigationBlock =
		languageSwitcher.closest('.block-navigation');
	const gtranslateLinks = languageSwitcher.querySelectorAll('.glink');

	languageSwitcher?.classList.toggle('active');
	currentNavigationBlock?.classList.toggle('active');

	if (languageSwitcher?.classList.contains('active')) {
		//closeActiveSubmenu();
		translationToggleDropdownButton.setAttribute('aria-expanded', true);
		document.addEventListener('click', clickOffLanguageSwitcher);
		if (contentArea) {
			contentArea.classList.add('active');
		}

		gtranslateLinks.forEach((link) => {
			link.addEventListener('click', handleGtranslateClick);
		});
	} else {
		translationToggleDropdownButton.setAttribute('aria-expanded', false);
		gtranslateLinks.forEach((link) => {
			link.removeEventListener('click', handleGtranslateClick);
		});
		if (contentArea) {
			contentArea.classList.remove('active');
		}
	}
};

const clickOffLanguageSwitcher = (e) => {
	//console.log(e);
	const languageSwitcher = translationToggleDropdownButton.closest(
		'.navigation-language'
	);
	const currentNavigationBlock =
		languageSwitcher.closest('.block-navigation');

	if (!languageSwitcher.contains(e.target)) {
		if (contentArea) {
			contentArea.classList.remove('active');
		}
		languageSwitcher.classList.remove('active');
		currentNavigationBlock?.classList.remove('active');
		translationToggleDropdownButton.setAttribute('aria-expanded', false);
		document.removeEventListener('click', clickOffLanguageSwitcher);
	}
};

if (navigationBlocks.length > 0) {
	navigationBlocks.forEach((navigationBlock) => {
		navigationBlock.classList.add('block-navigation--initialized');
	});
}

// if (gtranslateLinks.length > 0) {
// 	console.log('Found gtranslate links:', gtranslateLinks.length);
// 	gtranslateLinks.forEach((link) => {
// 		console.log('Adding click listener to gtranslate link');
// 		link.addEventListener('click', toggleLanguageDropdown);
// 	});
// }

window.addEventListener('navigation-submenu-closed', submenuClosed);
window.addEventListener('navigation-submenu-closed', setNavMenuActiveClass);
window.addEventListener('navigation-submenu-opened', setNavMenuActiveClass);
window.addEventListener(
	'resize',
	throttle(setFirstLevelButtonBlockOffsets, 100)
);
/** Translation Toggle Gtranslate */
translationToggleDropdownButton?.addEventListener(
	'click',
	toggleLanguageDropdown
);

/**
 * GTranslate injects its language links asynchronously, so watch for
 * when they appear (or the active language changes) and keep the
 * button label in sync.
 */
const gtranslateDropdown = document.querySelector('.gtranslate_dropdown');

if (gtranslateDropdown) {
	updateTranslationButtonLabel();

	const gtranslateObserver = new MutationObserver(
		updateTranslationButtonLabel
	);

	gtranslateObserver.observe(gtranslateDropdown, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['class'],
	});
}
